<template>
  <button
    :type="type"
    class="button-v2 inline-flex flex-row items-center justify-center shrink-0 whitespace-nowrap m-0 transition-colors duration-150 outline-none focus:outline-none"
    :class="buttonClasses"
    :disabled="disabled && !loading"
    :aria-busy="loading ? 'true' : null"
    v-on="listeners"
  >
    <spinner
      v-if="loading"
      size="small"
      :color-scheme="variant === 'filled' ? '' : 'dark'"
      class="!p-0 !m-0"
    />
    <span v-else-if="$slots.icon" class="inline-flex shrink-0">
      <slot name="icon" />
    </span>
    <span
      v-else-if="icon && iconPosition === 'start'"
      class="material-symbols-outlined leading-none"
      :class="[iconSizeClass, iconClass]"
    >
      {{ icon }}
    </span>
    <span v-if="hasLabel" class="truncate">
      <slot />
    </span>
    <span
      v-if="icon && iconPosition === 'end' && !loading"
      class="material-symbols-outlined leading-none"
      :class="[iconSizeClass, iconClass]"
    >
      {{ icon }}
    </span>
  </button>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';

const VARIANT_CLASSES = {
  filled: 'button-filled',
  outline: 'button-outline',
  ghost: 'button-ghost',
  naked: 'button-naked',
};

const COLOR_CLASSES = {
  filled: { danger: 'btn-delete', warning: 'btn-warning' },
  outline: { danger: 'btn-outline-danger' },
  ghost: { danger: 'btn-danger' },
  naked: { neutral: 'btn-neutral' },
};

const SIZE_CLASSES = {
  sm: { label: 'h-8 px-3 gap-1 text-sm leading-5', icon: 'w-8 h-8' },
  md: {
    label: 'h-10 px-4 gap-1 text-sm leading-5',
    labelWithIcon: 'h-10 px-3 gap-1 text-sm leading-5',
    icon: 'w-10 h-10',
  },
  lg: { label: 'h-12 px-5 gap-2 text-base leading-6', icon: 'w-12 h-12' },
};

const ICON_SIZE_CLASSES = {
  sm: 'text-base',
  md: 'text-[24px]',
  lg: 'text-[24px]',
};

export default {
  name: 'ButtonV2',
  components: { Spinner },
  props: {
    variant: {
      type: String,
      default: 'filled',
      validator: value => Object.keys(VARIANT_CLASSES).includes(value),
    },
    color: {
      type: String,
      default: 'primary',
      validator: value =>
        ['primary', 'danger', 'warning', 'neutral'].includes(value),
    },
    size: {
      type: String,
      default: 'md',
      validator: value => Object.keys(SIZE_CLASSES).includes(value),
    },
    icon: { type: String, default: '' },
    iconPosition: {
      type: String,
      default: 'start',
      validator: value => ['start', 'end'].includes(value),
    },
    iconClass: { type: String, default: '' },
    type: { type: String, default: 'button' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
  },
  computed: {
    hasLabel() {
      return !!this.$slots.default;
    },
    isIconOnly() {
      return !this.hasLabel && !!this.icon;
    },
    iconSizeClass() {
      return ICON_SIZE_CLASSES[this.size];
    },
    buttonClasses() {
      const size = SIZE_CLASSES[this.size];
      const hasIcon = !!this.icon || !!this.$slots.icon;
      const label = (hasIcon && size.labelWithIcon) || size.label;
      return [
        VARIANT_CLASSES[this.variant],
        COLOR_CLASSES[this.variant]?.[this.color] || '',
        this.isIconOnly ? `${size.icon} p-0 only-icon` : label,
        { 'w-full': this.block, 'pointer-events-none': this.loading },
      ];
    },
    listeners() {
      return {
        ...this.$listeners,
        click: event => {
          if (this.loading) {
            event.preventDefault();
            return;
          }
          this.$emit('click', event);
        },
      };
    },
  },
};
</script>
